/**
 * Meeting-chat history.
 *
 * Authenticated with the ROOM token, not the app token. Every participant
 * holds one (it is what authenticates the Phoenix socket) including guests,
 * who have no app session at all — and it carries a `room_id` claim, so the
 * backend can check the caller belongs to the room being read rather than
 * merely being signed in somewhere.
 *
 * Live messages never come through here; they arrive on the room channel. This
 * is only what was said before you joined.
 */

import { config } from '@/config/env'
import { roomFetch } from '@/features/meeting/services/room-token'
import type { ChatMessage } from '../models/chat.model'
import { toChatMessage, type WireChatMessage } from './chat.service'

const API = config.apiUrl

/**
 * Fetch the archive for one room.
 *
 * Resolves to `[]` on any failure: history is a nicety, and a meeting whose
 * chat panel refuses to open because the backend hiccuped is worse than one
 * that starts empty. Live chat is unaffected either way.
 */
export async function fetchMeetingChatHistory(
  roomId: string,
  roomToken: string,
  limit = 200,
): Promise<ChatMessage[]> {
  if (!roomId || !roomToken) return []

  try {
    const res = await roomFetch(
      roomId,
      roomToken,
      `${API}/api/v1/meeting-chat/${encodeURIComponent(roomId)}/messages?limit=${limit}`,
    )
    if (!res.ok) return []

    const body = (await res.json()) as { messages?: WireChatMessage[] }
    return (body.messages ?? []).map(toChatMessage)
  } catch {
    return []
  }
}
